import { ApolloQueryResult } from '@apollo/client';
import differenceInDays from 'date-fns/differenceInCalendarDays';
import { difference } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import { OptionsType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import Textfield from '@atlaskit/textfield';
import { AnalyticsTrackAction, AnalyticsTrackSubject, analytics } from '@atlassian/temp-analytics';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { createCreditNotesCCP, updateEntitlementPlan } from '@atlassian/temp-proxies';
import {
  CreateCreditNoteRequest,
  CreditNoteRequestMetaData,
  CreditNotetype,
  FinanceReasonCodeSelect,
  FinanceRefundReasonCode,
  FinanceRefundReasons,
  InvoiceLineItem,
  LineItemSelect,
  MAX_MEMO_LENGTH,
  PaymentMethod,
  ProductAppLineItemData,
  ReasonCodeSelect,
  RefundReasonCode,
  RefundReasons,
} from '@atlassian/cssxp-types-billing';
import { UpdateOrderRequest } from '@atlassian/cssxp-typescommon';
import { ENABLE_SUPPORT_TICKET_AND_NOTE } from '@atlassian/cssxp-utils-generic';
import {
  ApprovalEntityInput as ApprovalEntityInfoInput,
  ApprovalType,
  EntityType,
  RefundType,
} from '@css-xp/apd-common';
import { ApprovalFormSection, useApprovals } from '@css-xp/apd-process-manager';
import { TaskDetailAction, useTaskList } from '@css-xp/apd-task-manager';
import {
  ErrorComponents,
  InfoPanel,
  InvoiceFinanceRefundReasonFormField,
  InvoiceRefundReasonFormField,
  MultiCheckboxSelectLineItemFormField,
  NotificationTypes,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import ConfirmationDialog from './components/refund-and-rebill-confirmation-dialog';
import './invoice-refund-ccp-pane.scss';

interface Props {
  invoiceId: string;
  invoiceNumber: string;
  transactionAccountId: string;
  taxExempt?: boolean;
  refundType: CreditNotetype;
  isRefundLineItems: boolean;
  lineItems?: Array<InvoiceLineItem>;
  paymentMethod?: PaymentMethod;
  invoiceStatus?: string;
  invoiceCreated?: number;
  coupledLineItems?: ProductAppLineItemData;
  refetch?: () => Promise<ApolloQueryResult<any>>;
}

export interface ApprovalEntityInput extends ApprovalEntityInfoInput {
  billingStartDate?: string;
}

interface FormData {
  memo: string;
  financeRefundReason: FinanceReasonCodeSelect;
  lineItems?: Array<LineItemSelect>;
  internalNote: string | null;
  supportTicketNumber: string | null;
  comments?: string;
  refundReason?: ReasonCodeSelect;
}

interface ConstructRequestDataProps {
  transactionAccountId: string;
  invoiceId: string;
  refundType: CreditNotetype;
  entitlementIds: string[] | null;
  paymentMethod: string | null | undefined;
  formData: FormData;
  invoiceStatus: string | null | undefined;
}

enum RefundHeader {
  FULL_ITEM_REFUND = 'Refund full invoice',
  LINE_ITEM_REFUND = 'Refund line items',
  TAX_ONLY_REFUND = 'Refund tax',
}

enum WarningMessage {
  TAX_ONLY_REFUND = 'Tax Refunds are subject to evaluation of the Transaction Account being tax exempt. When the request to refund the tax amount is taken, the evaluation will happen offline. Requests determined to be ineligible will result in a full invoice refund and recharge of the refunded amount. Please confirm the customer is tax exempt before proceeding.',
  NOT_TAX_EXEMPT = 'The Ship-to party on this invoice is not tax-exempt',
}

const constructMetadata = (formData: FormData, isRebillChecked = false) => {
  const metadata: Record<string, string> = {};
  if (formData.supportTicketNumber) {
    metadata[CreditNoteRequestMetaData.SUPPORT_TICKET_NUMBER] = formData.supportTicketNumber;
  }
  if (formData.financeRefundReason.value !== 'NONE') {
    metadata[CreditNoteRequestMetaData.FINANCE_REFUND_REASON] = formData.financeRefundReason.value;
  }
  if (isRebillChecked) {
    metadata['handNote'] = formData.memo;
  }
  return metadata;
};

const constructRefundRequestData = ({
  transactionAccountId,
  invoiceId,
  refundType,
  entitlementIds,
  paymentMethod,
  formData,
  invoiceStatus,
}: ConstructRequestDataProps) => {
  const requestData: CreateCreditNoteRequest = {
    transactionAccountId: transactionAccountId,
    invoice: invoiceId,
    memo: formData.memo,
    type: refundType,
    //@ts-ignore
    reason: formData.refundReason.value.toLowerCase(),
    //@ts-ignore
    entitlementIds: entitlementIds,
    paymentMethod: paymentMethod,
    metadata: constructMetadata(formData),
    invoiceStatus: invoiceStatus,
  };
  return requestData;
};

const constructRebillRequestData = (
  entitlementIds: string[] | [],
  formData: FormData,
  lineItems: InvoiceLineItem[]
) => {
  const filterLineItems = lineItems.filter((lineItem) => {
    const refinedLineItem = entitlementIds.filter((entId) => lineItem.entitlementId === entId);
    return refinedLineItem.length ? true : false;
  });
  const bulkEntsDetails = filterLineItems.map((filterItem, index) => {
    return {
      entitlementId: entitlementIds[index],
      productId: filterItem.productKey,
      offeringId: filterItem.entitlement?.offeringId as string,
    };
  });
  const requestData: UpdateOrderRequest = {
    metadata: constructMetadata(formData, true),
    rebillOptions: {
      billFromTimeStamp: moment().valueOf(),
    },
    entitlementId: entitlementIds[0], //since this field is required on type, but will be ignored in service layer, since all the ent will be picked from linkedEnts keys.
    offeringId: filterLineItems[0].entitlement?.offeringId,
    productId: filterLineItems[0].productId ?? '',
    linkedEntitlements: bulkEntsDetails,
  };
  return requestData;
};

export const constructApprovalEntity = (item: InvoiceLineItem, noOfDaysForInvoice?: number): ApprovalEntityInput => {
  const billingStartDate = !noOfDaysForInvoice ? { billingStartDate: item.startDate } : {};
  return {
    entityId: item.entitlementNumber,
    entityType: EntityType.ENTITLEMENT,
    refundType: RefundType.LINE_ITEMS,
    noOfDays: noOfDaysForInvoice,
    ...billingStartDate,
  } as ApprovalEntityInput;
};

const getOptionsToRefundSelect = (lineItemsToRefund: InvoiceLineItem[]) => {
  return lineItemsToRefund.map((lineItem) => {
    const description = lineItem?.description ? ` (${lineItem?.description})` : '';
    const label = `${lineItem?.entitlementNumber}${description}` ?? '';
    const lineItemSelect: LineItemSelect = {
      label: label,
      value: lineItem?.id ?? '',
    };
    return lineItemSelect;
  });
};

export const InvoiceRefundCcpPane = ({
  invoiceId,
  invoiceNumber,
  transactionAccountId,
  refundType,
  isRefundLineItems,
  lineItems,
  taxExempt,
  paymentMethod,
  invoiceStatus,
  invoiceCreated,
  coupledLineItems,
  refetch,
}: Props) => {
  const isTaxRefund = refundType === CreditNotetype.TAX_ONLY_REFUND ? true : false;
  const [displayRebillOption, setDisplayRebillOption] = useState<boolean>(!isTaxRefund);
  const [selectedLineItem, setSelectedLineItem] = useState<LineItemSelect[]>([]);
  const [isRebillChecked, setIsRebillChecked] = useState<boolean>(false);
  const [formDataObj, setFormDataObj] = useState<FormData>();
  const [itemsToRefundList, setItemsToRefundList] = useState<string[] | null>(null);
  const [isRebillDisabled, setIsRebillDisabled] = useState<boolean>(isRefundLineItems);
  const [refundReason, setRefundReason] = useState<string | undefined>(undefined);
  const [isRefundCancelledPurchase, setRefundCancelledPurchase] = useState<boolean>(false);
  const [isOpenModal, setIsOpenModal] = useState(true);
  const closeModal = useCallback(() => setIsOpenModal(false), []);

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  const [isEnabled] = useFeatureToggle();
  const { closeSidebar } = useSidebar();

  const getItemsForApproval = useCallback(
    (lineItems: InvoiceLineItem[] = [], refundReason?: ReasonCodeSelect) => {
      const noOfDaysForInvoice = invoiceCreated ? differenceInDays(new Date(), new Date(invoiceCreated)) : 0;
      switch (refundType) {
        case CreditNotetype.FULL_ITEM_REFUND: {
          const items: ApprovalEntityInput[] = [
            {
              entityId: invoiceNumber,
              entityType: EntityType.INVOICE,
              noOfDays: noOfDaysForInvoice,
              refundType: isRefundLineItems ? RefundType.LINE_ITEMS : RefundType.FULL_INVOICE,
            },
          ];

          if (refundReason?.label === RefundReasons.REFUND_CANCELLED_PURCHASE) {
            return items.concat(lineItems.map((item) => constructApprovalEntity(item)));
          } else {
            if (!isRefundLineItems) return items;
            return items.concat(lineItems.map((item) => constructApprovalEntity(item, noOfDaysForInvoice)));
          }
        }
        case CreditNotetype.TAX_ONLY_REFUND: {
          return [
            {
              entityId: invoiceNumber,
              entityType: EntityType.INVOICE,
              noOfDays: noOfDaysForInvoice,
              refundType: RefundType.TAX_ONLY,
            },
          ];
        }
        case CreditNotetype.DUNNING_CANCELLATION_CREDIT:
        case CreditNotetype.CREDIT_GRANT: {
          // need to check with ccp added for typecheck
          return [];
        }
      }
    },
    [invoiceCreated, invoiceNumber, isRefundLineItems, refundType]
  );

  const approvalType = useMemo(() => {
    switch (refundType) {
      case CreditNotetype.FULL_ITEM_REFUND:
        return ApprovalType.SUBSCRIPTION_REFUND;
      case CreditNotetype.TAX_ONLY_REFUND:
        return ApprovalType.TAX_REFUND;
      case CreditNotetype.DUNNING_CANCELLATION_CREDIT:
      case CreditNotetype.CREDIT_GRANT:
        return ApprovalType.SUBSCRIPTION_REFUND; // need to check with ccp added for typecheck
    }
  }, [refundType]);

  const { hasOpenApprovalRequest, loading: taskListLoading, error: taskListError, data: taskListData } = useTaskList({
    businessKey: invoiceNumber,
    approvalType,
    skip: !invoiceNumber,
  });

  const scrollToApprovalSection = () => {
    const appr = document.getElementById('approval-task-section');
    appr ? appr.scrollIntoView({ behavior: 'smooth' }) : null;
  };

  const onApprovalComplete = useCallback(() => {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('approval-submit-success')
      .withHeader('Request submitted')
      .withMessage(
        <>
          The request has been created. You can view your request in the <strong>approval requests</strong> section.
          <br />
          <Link to="#" onClick={scrollToApprovalSection}>
            View approval request
          </Link>
        </>
      )
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch?.();
  }, [closeSidebar, refetch, sendSuccessNotification, setNotificationSent]);

  const {
    loading,
    approvalRequired,
    approvalError,
    approvalList,
    approvalSlackChannel,
    submitApproval,
    approvalProcessing,
    evaluate: evaluateApproval,
    setApprovalRequired,
  } = useApprovals({
    approvalType,
    onApprovalComplete,
  });

  let options: OptionsType<LineItemSelect>;

  const checkForDisableRebillingOption = (lineItemsToRefund: InvoiceLineItem[]) => {
    for (const lineItem of lineItemsToRefund) {
      if (lineItem.latestInvoiceDetails?.id !== invoiceId) {
        setDisplayRebillOption(false);
        break;
      }
    }
  };

  const coupledLineItemsList = useMemo(() => {
    const prodappItemsList: InvoiceLineItem[] = [];
    if (coupledLineItems) {
      const coupledItemKeys = Object.keys(coupledLineItems).slice(0, -1);
      coupledItemKeys.forEach((key) => {
        const currentKeyProducts = coupledLineItems[key as keyof typeof coupledLineItems]?.products ?? [];
        const currentKeyApps = coupledLineItems[key as keyof typeof coupledLineItems]?.apps ?? [];

        currentKeyProducts.forEach((product: InvoiceLineItem) => {
          prodappItemsList.push(product);
        });

        currentKeyApps.forEach((product: InvoiceLineItem) => {
          prodappItemsList.push(product);
        });
      });
    }
    return prodappItemsList;
  }, [coupledLineItems]);

  const coupledLineItemsListToRefunds = useMemo(() => {
    return coupledLineItemsList.filter((lineItem) => {
      return Number(lineItem?.subtotal) > 0;
    });
  }, [coupledLineItemsList]);

  const lineItemsToRefund = useMemo(() => {
    return (
      lineItems?.filter((lineItem) => {
        return Number(lineItem?.subtotal) > 0;
      }) ?? []
    );
  }, [lineItems]);

  useEffect(() => {
    if (!isTaxRefund && !isRefundLineItems) {
      if (coupledLineItemsListToRefunds.length) {
        checkForDisableRebillingOption(coupledLineItemsListToRefunds);
      } else {
        checkForDisableRebillingOption(lineItemsToRefund);
      }
    }
  }, [coupledLineItemsListToRefunds]);

  useEffect(() => {
    if (!isTaxRefund && isRefundLineItems && !!selectedLineItem?.length) {
      if (coupledLineItemsListToRefunds.length) {
        const filteredCoupledLineItems = selectedLineItem.map((selectedOption) => {
          return coupledLineItemsListToRefunds?.filter((lineItem) => lineItem.id === selectedOption.value)[0];
        });
        checkForDisableRebillingOption(filteredCoupledLineItems);
      } else {
        const filteredLineItems = selectedLineItem.map((selectedOption) => {
          return lineItemsToRefund?.filter((lineItem) => lineItem.id === selectedOption.value)[0];
        });
        checkForDisableRebillingOption(filteredLineItems);
      }
      setIsRebillDisabled(false);
    } else if (!isTaxRefund && isRefundLineItems && !selectedLineItem?.length) {
      setIsRebillDisabled(true);
    }
  }, [selectedLineItem, coupledLineItemsListToRefunds, lineItemsToRefund]);

  useEffect(() => {
    if (refundReason === RefundReasons.REFUND_CANCELLED_PURCHASE) {
      setRefundCancelledPurchase(true);
    }
  }, [refundReason]);

  const getRefundHeader = useCallback((): string => {
    switch (refundType) {
      case CreditNotetype.FULL_ITEM_REFUND:
        if (isRefundLineItems) {
          return RefundHeader.LINE_ITEM_REFUND;
        }
        return RefundHeader.FULL_ITEM_REFUND;
      case CreditNotetype.TAX_ONLY_REFUND:
        return RefundHeader.TAX_ONLY_REFUND;
      case CreditNotetype.DUNNING_CANCELLATION_CREDIT: // need to check with ccp added for typecheck
      case CreditNotetype.CREDIT_GRANT:
        return '';
    }
  }, [isRefundLineItems, refundType]);

  const refundTrackEvent = (approvalRequired: boolean, formData: FormData) => {
    analytics.sendTrackEvent({
      attributes: {
        refundType: getRefundHeader(),
        approvalRequired: approvalRequired,
        refundReason: formData.financeRefundReason.value.toLowerCase(),
        supportTicketNumber: formData.supportTicketNumber,
      },
      action: AnalyticsTrackAction.REFUND,
      actionSubject: AnalyticsTrackSubject.REFUND,
    });
  };

  async function handleRefundSubmmit(itemsToRefund: string[] | null, formData: FormData) {
    const requestDataForRefund = constructRefundRequestData({
      invoiceId,
      transactionAccountId,
      refundType,
      entitlementIds: itemsToRefund,
      //@ts-ignore
      paymentMethod,
      formData,
      invoiceStatus,
    });

    const lineItemsForApproval =
      lineItems?.filter((lineItem) => {
        return lineItem.id && itemsToRefund && itemsToRefund.includes(lineItem.id);
      }) ?? [];

    if (approvalRequired) {
      submitApproval({
        approvalType,
        businessKey: invoiceNumber,
        entityInformation: isRefundLineItems
          ? getItemsForApproval(lineItemsForApproval, formData.refundReason)
          : getItemsForApproval(lineItems, formData.refundReason),
        gsacId: formData.supportTicketNumber,
        initiatorComments: formData.comments,
        additionalInformation: requestDataForRefund,
      });
      //Send track event in case of Approval is Required - Need to check if this event is triggered even after failure
      refundTrackEvent(true, formData);
    } else {
      // check if approval required
      const evaluation = await evaluateApproval(getItemsForApproval(lineItemsForApproval, formData.refundReason));
      if (evaluation && !evaluation.isApprovalRequired) {
        submitRefundForm(requestDataForRefund);
        refundTrackEvent(false, formData);
      }
    }
  }

  const getItemsToRefund = (formData: FormData): string[] | null => {
    let items: string[] | null = null;
    if (refundType === CreditNotetype.FULL_ITEM_REFUND) {
      if (isRefundLineItems) {
        items = formData?.lineItems ? formData?.lineItems?.map((lineitem) => lineitem.value) : null;
      } else {
        items = lineItems?.map((lineitem) => lineitem.id ?? '') ?? null;
      }
    }
    return items;
  };

  const handleFormSubmit = async (formData: FormData) => {
    const itemsToRefund = getItemsToRefund(formData);
    const lineItemsListToRefund = coupledLineItemsListToRefunds.length
      ? coupledLineItemsListToRefunds
      : lineItemsToRefund;
    setRefundReason(formData?.refundReason?.label);
    setItemsToRefundList(itemsToRefund);
    setFormDataObj(formData);
    if (isRebillChecked) {
      const requestDataForRebill = constructRebillRequestData(itemsToRefund ?? [], formData, lineItemsListToRefund);
      submitRebillForm(requestDataForRebill);
      return;
    }
    await handleRefundSubmmit(itemsToRefund, formData);
  };

  const {
    error: refundFormError,
    isProcessing: isRefundProcessing,
    submitForm: submitRefundForm,
    complete: refundComplete,
  } = useFormSubmit<CreateCreditNoteRequest, any>({
    submitFormAction: createCreditNotesCCP,
    onComplete: () => {
      closeSidebar();
      // Fetch the fresh data for `billingDoc`
      refetch?.(); //should call the fetchInvoice
    },
  });

  const {
    error: rebillFormError,
    isProcessing: isRebillProcessing,
    submitForm: submitRebillForm,
    complete: rebillComplete,
  } = useFormSubmit<UpdateOrderRequest, any>({
    submitFormAction: updateEntitlementPlan,
    onComplete: () => {
      formDataObj && handleRefundSubmmit(itemsToRefundList, formDataObj as FormData);
    },
  });

  if (taskListLoading || loading) {
    return <LoadingIndicator />;
  }

  if (!transactionAccountId) {
    return (
      <InfoPanel
        type="error"
        message={
          <ErrorComponents.ErrorMessageOnInfoPanel
            title="We're unable to process refund as no transaction account found for this invoice"
            message="No trasaction account found"
          />
        }
      />
    );
  }

  if (refundComplete && !notificationSent) {
    const refundAndRebill = 'Refund and re-bill successfully initiated';
    const refund = 'Refund initiated successfully';
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('refund-processed-success')
      .withHeader(`${isRebillChecked ? refundAndRebill : refund}`)
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  if (isRefundLineItems) {
    if (coupledLineItemsListToRefunds.length) {
      options = getOptionsToRefundSelect(coupledLineItemsListToRefunds);
    } else if (lineItemsToRefund.length) {
      options = getOptionsToRefundSelect(lineItemsToRefund);
    } else {
      options = [];
    }
  }

  const mapReasonsForSelect = (reasonKeys: string[]) => {
    return reasonKeys.map(
      //@ts-ignore
      (key: RefundReasonCode) => {
        return {
          label: RefundReasons[key],
          value: key,
        };
      }
    );
  };

  const mapFinanceReasonsForSelect = (reasonKeys: string[]) => {
    return reasonKeys.map(
      //@ts-ignore
      (key: FinanceRefundReasonCode) => {
        return {
          label: FinanceRefundReasons[key],
          value: key,
        };
      }
    );
  };

  const filterReasonsForRefundType = (refundType: CreditNotetype): string[] => {
    switch (refundType) {
      case CreditNotetype.TAX_ONLY_REFUND:
        return ['REFUND_TAX'];
      default:
        return difference(Object.keys(RefundReasons), ['REFUND_TAX']);
    }
  };

  const filterFinanceReasonsForRefundType = (refundType: CreditNotetype): string[] => {
    switch (refundType) {
      case CreditNotetype.TAX_ONLY_REFUND:
        return ['REFUND_FOR_REBILL'];
      default:
        return difference(Object.keys(FinanceRefundReasons), ['REFUND_TAX']);
    }
  };

  const getRefundReasonOptions: OptionsType<ReasonCodeSelect> = mapReasonsForSelect(
    filterReasonsForRefundType(refundType)
  );

  const getFinanceRefundReasonOptions: OptionsType<FinanceReasonCodeSelect> = mapFinanceReasonsForSelect(
    filterFinanceReasonsForRefundType(refundType)
  );

  if (hasOpenApprovalRequest || !!taskListError) {
    return (
      <div className="invoice-refund-ccp-form" style={{ maxWidth: '600px' }}>
        <Form
          onSubmit={() => {
            //@ts-ignore
          }}
        >
          {({ formProps }) => (
            <form {...formProps}>
              <FormHeader title={getRefundHeader()}></FormHeader>
              <FormSection>
                {!!taskListError && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to process refund for the following reason"
                        message={taskListError.message}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {hasOpenApprovalRequest && (
                  <InfoPanel
                    type="warning"
                    message={
                      <div className="approval-request-inflight-banner">
                        One or more approval requests are currently in progress. If you perform this action, those
                        approval requests will be canceled automatically.
                        {taskListData?.taskList?.tasks?.length && (
                          <TaskDetailAction
                            task={taskListData.taskList.tasks[0]}
                            text="View approval request details"
                            refetch={refetch}
                            refetchAll={refetch}
                          />
                        )}
                      </div>
                    }
                  />
                )}
              </FormSection>
            </form>
          )}
        </Form>
      </div>
    );
  }

  return (
    <div className="invoice-refund-ccp-form" style={{ maxWidth: '600px' }}>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title={getRefundHeader()}></FormHeader>
            <FormSection>
              {!!refundFormError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to process refund for the following reason"
                      message={refundFormError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!!rebillFormError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to process rebill for the following reason"
                      message={rebillFormError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {!!approvalError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to process refund for the following reason"
                      message={approvalError.message}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {refundType === CreditNotetype.TAX_ONLY_REFUND && (
                <InfoPanel type="warning" message={WarningMessage.TAX_ONLY_REFUND} />
              )}
              <br />

              {taxExempt === false && <InfoPanel type="warning" message={WarningMessage.NOT_TAX_EXEMPT} />}
            </FormSection>
            <FormSection>
              <Field name="invoiceNumber" label="Invoice number">
                {() => <div>{invoiceNumber}</div>}
              </Field>
            </FormSection>

            <FormSection>
              <InvoiceRefundReasonFormField
                isRequired={true}
                options={getRefundReasonOptions}
                isDisabled={approvalRequired || getRefundReasonOptions.length === 1}
              />
            </FormSection>

            <FormSection>
              <InvoiceFinanceRefundReasonFormField
                options={getFinanceRefundReasonOptions}
                isDisabled={approvalRequired || getFinanceRefundReasonOptions.length === 1}
              />
            </FormSection>

            <FormSection>
              {isRefundLineItems && (
                <MultiCheckboxSelectLineItemFormField
                  options={options}
                  fieldName={`lineItems`}
                  isRequired={true}
                  isDisabled={approvalRequired}
                  setSelectedLineItem={setSelectedLineItem}
                />
              )}
            </FormSection>

            {isEnabled(ENABLE_SUPPORT_TICKET_AND_NOTE) && (
              <FormSection>
                <Field
                  name="supportTicketNumber"
                  label="Support ticket link"
                  isRequired
                  validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield {...fieldProps} name="supportTicketNumber" placeholder={'Add GSAC ticket number'} />
                      {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                    </>
                  )}
                </Field>
              </FormSection>
            )}

            <FormSection>
              <Field<string, HTMLTextAreaElement>
                name="memo"
                label="Internal memo"
                isRequired={true}
                validate={(value: any) => (!(value && value.length < MAX_MEMO_LENGTH) ? 'INVALID' : undefined)}
              >
                {({ fieldProps, error }) => (
                  <div>
                    <TextArea
                      {...fieldProps}
                      placeholder={`Add notes (Max limit: ${MAX_MEMO_LENGTH} characters, internal memo is not shown in PDF)`}
                      name="memo"
                      testId={'refund-update-memo-field'}
                      isRequired={true}
                      isInvalid={error ? true : false}
                    />
                    {error && <ErrorMessage>{`Max limit: ${MAX_MEMO_LENGTH} characters`}</ErrorMessage>}
                  </div>
                )}
              </Field>
            </FormSection>

            {displayRebillOption && (
              <FormSection>
                <Field name="isRebillAllow">
                  {({ fieldProps }) => (
                    <div className="rebill-checbox-wrapper">
                      <Checkbox
                        label="Re-bill"
                        onChange={(event) => {
                          fieldProps.onChange(event);
                          setIsRebillChecked((value) => !value);
                        }}
                        isChecked={isRebillChecked}
                        testId="rebill-cb-default"
                        isDisabled={isRebillDisabled}
                      />
                    </div>
                  )}
                </Field>
              </FormSection>
            )}

            {approvalRequired ? (
              <ApprovalFormSection
                approvalType={approvalType}
                isLineItemRefund={isRefundLineItems}
                slackChannel={approvalSlackChannel ?? ''}
                onCancel={() => closeSidebar()}
                isLoading={approvalProcessing}
                approvalItems={approvalList.filter((item) => item.approvalRequired && item.entityId !== invoiceNumber)}
                standardItems={approvalList.filter((item) => !item.approvalRequired && item.entityId !== invoiceNumber)}
                isInvoiceDateApproval={
                  approvalType === ApprovalType.SUBSCRIPTION_REFUND &&
                  !!approvalList.find((item) => item.approvalRequired && item.entityId === invoiceNumber)
                }
                onEdit={() => {
                  setApprovalRequired(false);
                  setRefundCancelledPurchase(false);
                }}
                isSubscriptionStartDateApproval={isRefundCancelledPurchase}
              />
            ) : (
              <FormFooter align="end">
                <ButtonGroup>
                  <Button appearance="default" onClick={() => closeSidebar()} testId="cancel-btn">
                    Cancel
                  </Button>
                  <LoadingButton
                    appearance="primary"
                    type="submit"
                    isLoading={isRefundProcessing || isRebillProcessing || approvalProcessing}
                    testId="process-refund"
                    isDisabled={!!approvalError}
                  >
                    {displayRebillOption && isRebillChecked ? 'Process refund & rebill' : 'Process refund'}
                  </LoadingButton>
                </ButtonGroup>
              </FormFooter>
            )}
          </form>
        )}
      </Form>
      <ConfirmationDialog isOpenModal={isOpenModal} isRebillChecked={isRebillChecked} closeModal={closeModal} />
    </div>
  );
};
